import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { adminService } from '../../services';
import { formatCurrency, formatDate } from '../../utils/formatters';
import PageHeader from '../../components/common/PageHeader';
import { API_BASE_URL } from '../../constants/config';
import api from '../../services/api';

const ManageVouchers = () => {
    const { t, i18n } = useTranslation();
    const lang = i18n.language;
    const [vouchers, setVouchers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [actionLoading, setActionLoading] = useState(null);
    const [previewImage, setPreviewImage] = useState(null);
    const [confirmDonation, setConfirmDonation] = useState(null);
    const [confirmRejectDonation, setConfirmRejectDonation] = useState(null);
    const [rejectReason, setRejectReason] = useState('');
    const [voucherObjectUrls, setVoucherObjectUrls] = useState({}); // donationId -> blob:ObjectURL
    const [voucherBlobLoading, setVoucherBlobLoading] = useState({}); // donationId -> boolean

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(null); // may be null if API doesn't return totals
    const limit = 10;

    const isPdfVoucher = (v) => {
        if (!v) return false;
        const s = String(v);
        return s.startsWith('data:application/pdf') || s.toLowerCase().includes('.pdf');
    };

    const getAbsoluteVoucherUrl = (value) => {
        if (!value) return null;
        const v = String(value);
        if (v.startsWith('data:') || v.startsWith('http://') || v.startsWith('https://')) return v;

        // In this project API_BASE_URL typically ends with "/main/api/v1".
        // Backend often returns paths like "/main/api/v1/public/voucher-files/...".
        const originBase = API_BASE_URL.includes('/main/api/v1')
            ? API_BASE_URL.split('/main/api/v1')[0]
            : API_BASE_URL.replace(/\/+$/, '');

        if (v.startsWith('/')) return `${originBase}${v}`;
        return v;
    };

    const fetchVouchers = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const response = await adminService.getDonationsList(
                {
                    status: 'PENDING',
                    paymentMethod: 'VOUCHER',
                    limit: limit,
                    offset: (currentPage - 1) * limit,
                    sortBy: 'createdAt',
                    order: 'ASC'
                },
                true
            ); // isAdmin = true

            const payload = response?.data || response || {};
            const items = payload?.items || [];
            const totalRaw =
                payload?.totalCount ??
                payload?.total ??
                payload?.count ??
                payload?.totalItems;

            setVouchers(items);
            setTotalCount(totalRaw === undefined || totalRaw === null ? null : (Number(totalRaw) || 0));
        } catch (err) {
            console.error('Error fetching vouchers:', err);
            setError(t('admin.vouchers.messages.loadError'));
        } finally {
            setLoading(false);
        }
    }, [currentPage, t]);

    useEffect(() => {
        fetchVouchers();
    }, [fetchVouchers]);

    useEffect(() => {
        return () => {
            Object.values(voucherObjectUrls).forEach((url) => {
                try {
                    URL.revokeObjectURL(url);
                } catch (e) {
                    // noop
                }
            });
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        // Auto-refresh so "other device" uploads show without manual refresh.
        const onFocus = () => fetchVouchers();
        const onVisibility = () => {
            if (!document.hidden) fetchVouchers();
        };

        window.addEventListener('focus', onFocus);
        document.addEventListener('visibilitychange', onVisibility);

        const intervalId = window.setInterval(() => {
            if (!document.hidden) fetchVouchers();
        }, 12000);

        return () => {
            window.removeEventListener('focus', onFocus);
            document.removeEventListener('visibilitychange', onVisibility);
            window.clearInterval(intervalId);
        };
    }, [fetchVouchers]);

    const fetchVoucherBlobUrl = async (donation) => {
        if (!donation?.id) return null;
        if (voucherObjectUrls[donation.id]) return voucherObjectUrls[donation.id];
        if (voucherBlobLoading[donation.id]) return null;

        const rawPath = donation.voucherImageUrl || donation.voucherImage;
        if (!rawPath || String(rawPath).startsWith('data:')) return null;

        try {
            setVoucherBlobLoading((prev) => ({ ...prev, [donation.id]: true }));
            const res = await api.get(rawPath, { responseType: 'blob' });
            const blobUrl = URL.createObjectURL(res.data);
            setVoucherObjectUrls((prev) => ({ ...prev, [donation.id]: blobUrl }));
            return blobUrl;
        } catch (e) {
            // If this fails too, backend is likely not serving the file as expected.
            return null;
        } finally {
            setVoucherBlobLoading((prev) => ({ ...prev, [donation.id]: false }));
        }
    };

    const handleApprove = async () => {
        if (!confirmDonation) return;
        const id = confirmDonation.id;

        try {
            setActionLoading(id);
            // Optimistically remove from list so it disappears instantly
            setVouchers((prev) => prev.filter((d) => d.id !== id));
            setTotalCount((prev) => Math.max(0, prev - 1));
            setConfirmDonation(null);

            await adminService.approveVoucher(id);
            setSuccessMessage('Voucher approved and payment marked as successful.');
        } catch (err) {
            console.error('Error approving voucher:', err);
            setError(err.response?.data?.message || t('admin.vouchers.messages.approveError'));
            // Re-sync list if something went wrong
            fetchVouchers();
        } finally {
            setActionLoading(null);
        }
    };

    const handleReject = async () => {
        if (!confirmRejectDonation) return;
        const id = confirmRejectDonation.id;

        try {
            setActionLoading(id);
            // Optimistically remove from list so it disappears instantly
            setVouchers((prev) => prev.filter((d) => d.id !== id));
            setTotalCount((prev) => Math.max(0, prev - 1));
            setConfirmRejectDonation(null);
            setRejectReason('');

            await adminService.rejectVoucher(id, rejectReason?.trim() ? { reason: rejectReason.trim() } : {});
            setSuccessMessage(t('admin.vouchers.messages.rejectSuccess'));
        } catch (err) {
            console.error('Error rejecting voucher:', err);
            setError(err.response?.data?.message || t('admin.vouchers.messages.rejectError'));
            // Re-sync list if something went wrong
            fetchVouchers();
        } finally {
            setActionLoading(null);
        }
    };

    const hasKnownTotal = typeof totalCount === 'number' && Number.isFinite(totalCount);
    const totalPages = hasKnownTotal ? Math.ceil(totalCount / limit) : null;
    const hasNextPage = hasKnownTotal ? currentPage < totalPages : vouchers.length === limit;

    if (loading && vouchers.length === 0) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-primary-50 pb-8">
            <PageHeader
                title={t('admin.vouchers.title')}
                subtitle={t('admin.vouchers.subtitle')}
                icon={
                    <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                }
            />
            <div className="max-w-7xl mx-auto px-4">
                <div className="mb-8 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                    {successMessage && (
                        <div className="w-full md:w-auto px-4 py-2 bg-green-50 border border-green-200 text-green-800 rounded-lg text-sm">
                            {successMessage}
                        </div>
                    )}
                    <button
                        onClick={fetchVouchers}
                        className="flex items-center px-4 py-2 bg-white border border-primary-300 rounded-lg shadow-sm hover:bg-primary-50 text-primary-700"
                    >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        {t('admin.vouchers.refresh')}
                    </button>
                </div>

                {error && (
                    <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
                        {error}
                    </div>
                )}

                <div className="bg-white rounded-lg shadow-md overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full">
                            <thead className="bg-gray-100 border-b-2 border-gray-200">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        {t('admin.vouchers.table.date')}
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        {t('admin.vouchers.table.donor')}
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        {t('admin.vouchers.table.target')}
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        {t('admin.vouchers.table.amount')}
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        {t('admin.vouchers.table.voucherImage')}
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        {t('admin.vouchers.table.actions')}
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {vouchers.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                                            {t('admin.vouchers.table.noPending')}
                                        </td>
                                    </tr>
                                ) : (
                                    vouchers.map((donation) => (
                                        <tr key={donation.id} className="hover:bg-primary-50/50 transition-colors">
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm text-gray-900">
                                                    {formatDate(donation.createdAt, lang)}
                                                </div>
                                                <div className="text-xs text-gray-500">
                                                    {new Date(donation.createdAt).toLocaleTimeString(lang === 'ne' ? 'ne-NP' : 'en-US')}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm font-medium text-gray-900">
                                                    {donation.isAnonymous ? t('admin.vouchers.table.anonymous') : (donation.donorName || donation.donor?.fullName || t('admin.vouchers.table.anonymous'))}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm text-gray-900">
                                                    {donation.targetType === 'PARTY' ? t('admin.vouchers.table.gurukulFund') : (donation.candidateName || donation.candidate?.name || t('admin.vouchers.table.gurukul'))}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm font-semibold text-gray-900">
                                                    {formatCurrency(donation.amount, lang)}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                {(() => {
                                                    const raw = donation.voucherImageUrl || donation.voucherImage;
                                                    const voucherUrl = voucherObjectUrls[donation.id] || getAbsoluteVoucherUrl(raw);
                                                    return voucherUrl ? (
                                                    isPdfVoucher(voucherUrl) ? (
                                                        <a
                                                            href={voucherUrl}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            className="inline-flex items-center gap-2 text-primary-700 font-semibold bg-primary-50 px-3 py-2 rounded-lg border border-primary-200 hover:bg-primary-100 transition"
                                                            onClick={async (e) => {
                                                                // If backend mistakenly protects "public" URL, this ensures admins can still view it.
                                                                if (!voucherObjectUrls[donation.id] && raw && !String(raw).startsWith('data:')) {
                                                                    e.preventDefault();
                                                                    const blobUrl = await fetchVoucherBlobUrl(donation);
                                                                    if (blobUrl) window.open(blobUrl, '_blank', 'noopener,noreferrer');
                                                                    else window.open(voucherUrl, '_blank', 'noopener,noreferrer');
                                                                }
                                                            }}
                                                        >
                                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 16v-8m0 8l-3-3m3 3l3-3M4 20h16" />
                                                            </svg>
                                                            View PDF
                                                        </a>
                                                    ) : (
                                                    <button
                                                        type="button"
                                                        className="relative group w-20 h-14 focus:outline-none"
                                                        onClick={() => setPreviewImage(voucherUrl)}
                                                    >
                                                        <img
                                                            src={voucherUrl}
                                                            alt="Voucher"
                                                            className="w-full h-full object-cover rounded border border-gray-200 cursor-pointer group-hover:opacity-75"
                                                            onError={async () => {
                                                                // Retry via authenticated fetch (handles 401 on "public" URLs)
                                                                if (!voucherObjectUrls[donation.id] && raw && !String(raw).startsWith('data:')) {
                                                                    await fetchVoucherBlobUrl(donation);
                                                                }
                                                            }}
                                                        />
                                                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-0 group-hover:opacity-100">
                                                            <svg className="w-5 h-5 text-white drop-shadow-md" fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                                                            </svg>
                                                        </div>
                                                    </button>
                                                    )
                                                ) : (
                                                    <span className="text-gray-400 text-xs">{t('admin.vouchers.table.noImage')}</span>
                                                );
                                                })()}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                <div className="flex items-center gap-2">
                                                    <button
                                                        onClick={() => setConfirmDonation(donation)}
                                                        disabled={actionLoading === donation.id}
                                                        className="text-green-600 hover:text-green-900 font-semibold bg-green-50 px-3 py-1 rounded-full border border-green-200 hover:bg-green-100 transition disabled:opacity-50 flex items-center"
                                                    >
                                                        {actionLoading === donation.id && (
                                                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-green-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                            </svg>
                                                        )}
                                                        {t('admin.vouchers.actions.approve')}
                                                    </button>
                                                    <button
                                                        onClick={() => {
                                                            setConfirmRejectDonation(donation);
                                                            setRejectReason('');
                                                        }}
                                                        disabled={actionLoading === donation.id}
                                                        className="text-red-600 hover:text-red-900 font-semibold bg-red-50 px-3 py-1 rounded-full border border-red-200 hover:bg-red-100 transition disabled:opacity-50 flex items-center"
                                                    >
                                                        {t('admin.vouchers.actions.reject')}
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {(hasKnownTotal ? totalPages > 1 : (currentPage > 1 || hasNextPage)) && (
                        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
                            <div className="flex-1 flex justify-between sm:hidden">
                                <button
                                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                                    disabled={currentPage === 1}
                                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-primary-50 disabled:opacity-50"
                                >
                                    {t('actions.previous')}
                                </button>
                                <button
                                    onClick={() => setCurrentPage(currentPage + 1)}
                                    disabled={!hasNextPage}
                                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-primary-50 disabled:opacity-50"
                                >
                                    {t('actions.next')}
                                </button>
                            </div>
                            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                                <div>
                                    <p className="text-sm text-gray-700">
                                        {hasKnownTotal ? (
                                            <>
                                                {t('common.pagination.showing')} <span className="font-medium">{(currentPage - 1) * limit + 1}</span> {t('common.pagination.to')}{' '}
                                                <span className="font-medium">{Math.min(currentPage * limit, totalCount)}</span> {t('common.pagination.of')}{' '}
                                                <span className="font-medium">{totalCount}</span> {t('common.pagination.results')}
                                            </>
                                        ) : (
                                            <>
                                                Page <span className="font-medium">{currentPage}</span>
                                            </>
                                        )}
                                    </p>
                                </div>
                                <div>
                                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                                        <button
                                            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                                            disabled={currentPage === 1}
                                            className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                                        >
                                            {t('actions.previous')}
                                        </button>
                                        {hasKnownTotal ? (() => {
                                            const maxButtons = 5;
                                            let startPage = Math.max(1, currentPage - Math.floor(maxButtons / 2));
                                            let endPage = Math.min(totalPages, startPage + maxButtons - 1);

                                            if (endPage - startPage + 1 < maxButtons) {
                                                startPage = Math.max(1, endPage - maxButtons + 1);
                                            }

                                            const pages = [];
                                            for (let i = startPage; i <= endPage; i++) {
                                                pages.push(i);
                                            }

                                            return pages.map((pageNum) => (
                                                <button
                                                    key={pageNum}
                                                    onClick={() => setCurrentPage(pageNum)}
                                                    className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${currentPage === pageNum
                                                        ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                                                        : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                                                        }`}
                                                >
                                                    {pageNum}
                                                </button>
                                            ));
                                        })() : null}
                                        <button
                                            onClick={() => setCurrentPage(currentPage + 1)}
                                            disabled={!hasNextPage}
                                            className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                                        >
                                            {t('actions.next')}
                                        </button>
                                    </nav>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Fullscreen voucher preview */}
            {previewImage && (
                <div className="fixed inset-0 z-50 bg-black bg-opacity-80 flex items-center justify-center p-4 overflow-y-auto">
                    <button
                        type="button"
                        className="absolute top-4 right-4 z-[60] text-white hover:text-gray-200"
                        onClick={() => setPreviewImage(null)}
                    >
                        <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                    <div className="relative my-auto inline-block max-h-[min(92vh,92dvh)] max-w-[min(100vw-2rem,72rem)] rounded-xl shadow-2xl overflow-hidden align-middle">
                        <img
                            src={previewImage}
                            alt="Voucher Preview"
                            className="block max-h-[min(85vh,85dvh)] max-w-full w-auto h-auto object-contain"
                        />
                    </div>
                </div>
            )}

            {/* Confirm approve modal */}
            {confirmDonation && (
                <div className="fixed inset-0 z-40 flex items-center justify-center bg-black bg-opacity-40 px-4">
                    <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
                        <h2 className="text-lg font-semibold text-gray-900 mb-2">
                            {t('admin.vouchers.actions.approve')}
                        </h2>
                        <p className="text-sm text-gray-700 mb-4">
                            {t('admin.vouchers.actions.confirmApprove')}
                        </p>
                        <div className="flex justify-end gap-3 mt-4">
                            <button
                                type="button"
                                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 text-sm"
                                onClick={() => setConfirmDonation(null)}
                                disabled={!!actionLoading}
                            >
                                {t('actions.cancel')}
                            </button>
                            <button
                                type="button"
                                className="px-4 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700 text-sm flex items-center disabled:opacity-50"
                                onClick={handleApprove}
                                disabled={!!actionLoading}
                            >
                                {actionLoading && (
                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                )}
                                {t('admin.vouchers.actions.approve')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Confirm reject modal */}
            {confirmRejectDonation && (
                <div className="fixed inset-0 z-40 flex items-center justify-center bg-black bg-opacity-40 px-4">
                    <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
                        <h2 className="text-lg font-semibold text-gray-900 mb-2">
                            {t('admin.vouchers.actions.reject')}
                        </h2>
                        <p className="text-sm text-gray-700 mb-4">
                            {t('admin.vouchers.actions.confirmReject')}
                        </p>

                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                {t('admin.vouchers.actions.rejectReason')}
                            </label>
                            <textarea
                                value={rejectReason}
                                onChange={(e) => setRejectReason(e.target.value)}
                                rows={3}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                                placeholder={t('admin.vouchers.actions.rejectReasonPlaceholder')}
                            />
                        </div>

                        <div className="flex justify-end gap-3 mt-4">
                            <button
                                type="button"
                                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 text-sm"
                                onClick={() => {
                                    setConfirmRejectDonation(null);
                                    setRejectReason('');
                                }}
                                disabled={!!actionLoading}
                            >
                                {t('actions.cancel')}
                            </button>
                            <button
                                type="button"
                                className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 text-sm flex items-center disabled:opacity-50"
                                onClick={handleReject}
                                disabled={!!actionLoading}
                            >
                                {actionLoading && (
                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                )}
                                {t('admin.vouchers.actions.reject')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ManageVouchers;
